import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, AlertCircle, Loader2 } from 'lucide-react';

interface SocialAuthPageProps {
  onSocialAuth: (provider: string, token: string) => Promise<{ success: boolean; message?: string }>;
}

export function SocialAuthPage({ onSocialAuth }: SocialAuthPageProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSocialAuth = async (provider: string) => {
    setLoading(provider);
    setError(null);

    try {
      // ✅ NEW: Use manual OAuth flow (no Passport.js)
      // Each provider has its own route: /api/auth/linkedin, /api/auth/google, /api/auth/github
      const isLocalhost = window.location.hostname === 'localhost';
      const apiUrl = isLocalhost 
        ? 'http://localhost:5000' 
        : window.location.origin;
      
      console.log(`Redirecting to: ${apiUrl}/api/auth/${provider}`);
      window.location.href = `${apiUrl}/api/auth/${provider}`;
    } catch (err) {
      setError('Authentication failed. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div className="auth-page" style={{ paddingTop: '2rem' }}>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
              <BookOpen className="logo-icon" />
              <h1>recberry</h1>
            </div>
            <h2>Access Full Module 1</h2>
            <p>Authenticate to access the first module of our FREE course</p>
          </div>

          {error && (
            <div className="auth-alert error">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div className="auth-form">
            <div className="form-fields">
              {/* LinkedIn */}
              <button
                type="button"
                onClick={() => handleSocialAuth('linkedin')}
                disabled={loading === 'linkedin'}
                className="social-auth-btn"
              >
                {loading === 'linkedin' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                )}
                <span>{loading === 'linkedin' ? 'Connecting...' : 'Continue with LinkedIn'}</span>
              </button>

              {/* Google */}
              <button
                type="button"
                onClick={() => handleSocialAuth('google')}
                disabled={loading === 'google'}
                className="social-auth-btn"
              >
                {loading === 'google' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <svg className="w-3 h-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                <span>{loading === 'google' ? 'Connecting...' : 'Continue with Google'}</span>
              </button>

              {/* GitHub */}
              <button
                type="button"
                onClick={() => handleSocialAuth('github')}
                disabled={loading === 'github'}
                className="social-auth-btn"
              >
                {loading === 'github' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                )}
                <span>{loading === 'github' ? 'Connecting...' : 'Continue with GitHub'}</span>
              </button>

            </div>
          </div>

          <div className="auth-footer">
            <div style={{ 
              textAlign: 'center', 
              marginTop: '2rem', 
              paddingTop: '2rem', 
              borderTop: '1px solid #eee',
              color: 'var(--gray)',
              fontSize: '0.875rem'
            }}>
              <p style={{ margin: '0 0 1rem 0' }}>
                No account creation required. Just authenticate to access Module 1.
              </p>
              
              <button
                onClick={() => {
                  const modal = document.getElementById('privacy-modal');
                  if (modal) modal.style.display = 'block';
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--orange)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontFamily: 'var(--montserrat)'
                }}
              >
                Privacy & Data Protection
              </button>
            </div>
            
            <Link 
              to="/" 
              style={{ 
                color: 'var(--gray)', 
                textDecoration: 'none',
                fontWeight: 400,
                marginTop: '1rem',
                display: 'inline-block',
                fontSize: '0.8rem'
              }}
            >
              ← Back to Course Preview
            </Link>
          </div>
        </div>
      </div>
      
      {/* Privacy Modal */}
      <div 
        id="privacy-modal" 
        style={{
          display: 'none',
          position: 'fixed',
          zIndex: 1000,
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            (e.target as HTMLElement).style.display = 'none';
          }
        }}
      >
        <div style={{
          backgroundColor: '#fff',
          margin: '5% auto',
          padding: '2rem',
          border: '1px solid #ddd',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'auto',
          position: 'relative'
        }}>
          <button
            onClick={() => {
              const modal = document.getElementById('privacy-modal');
              if (modal) modal.style.display = 'none';
            }}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
          
          <h3 style={{ marginTop: 0, color: 'var(--dark)', fontFamily: 'var(--montserrat)' }}>
            Privacy & Data Protection
          </h3>
          
          <div style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#333' }}>
            <p style={{ marginBottom: '1.5rem', fontWeight: '600' }}>
              <strong>Consent with the processing of personal data</strong><br/>
              This document outlines the method of processing personal data based on the consent provided by data subjects for the purpose of receiving newsletters and engaging in other marketing activities conducted by the data administrator.
            </p>
            
            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1rem' }}>1. Identity and Contact Information of the Data Administrator</h4>
            <p style={{ fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
              Your data administrator is <strong>Recberry s.r.o.</strong> | IC: 04972236 | DIC: CZ04972236 | Tupolevova 741, Prague 9 Letnany | Incorporated in the Commercial Register kept by the City: Court in Prague, Section C, under file No.256441 (hereinafter referred to as the "Administrator")
            </p>
            <p style={{ fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
              <strong>Administrator's Mailing Address:</strong> Recberry s.r.o. | IC: 04972236 | DIC: CZ04972236 | Jana Zajice 2016, 258 01 Vlasim, Czechia<br/>
              <strong>Email Address:</strong> barbora@recberry.com<br/>
              <strong>Telephone:</strong> +420 702 080 993
            </p>
            <p style={{ fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
              The Administrator has not appointed a person authorized to process personal data.
            </p>
            
            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1rem' }}>2. Legal Basis for Data Processing</h4>
            <p style={{ fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
              The legal basis for processing your personal data is your consent given to the Administrator in accordance with Article 6 (1) (a) of the European Regulation of the Parliament and Council (EC) No. 2016/679 on the protection of individuals with regard to the processing of personal data and on the free movement of data, and repealing Directive No. 95/46 / EC (General Data Protection Regulation).
            </p>
            
            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1rem' }}>3. Purpose of Personal Data Processing</h4>
            <p style={{ fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
              The purpose of processing your personal data is to contact you, send you newsletters and perform other marketing activities on behalf of the Administrator. There is no automatic individual decision-making process carried out by the Administrator under Article 22 of the Regulation.
            </p>
            
            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1rem' }}>4. Personal Data Storage Period</h4>
            <p style={{ fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
              Your personal data will be stored for a duration of 5 years.
            </p>
            
            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1rem' }}>5. Other Recipients of Your Personal Data</h4>
            <p style={{ fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
              Other recipients of your personal data are individuals who provide marketing services for the Administrator. The Administrator has no intention to transfer your personal data to a third country (non-EU country) or to an international organization.
            </p>
            
            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1rem' }}>6. Data Subject Rights</h4>
            <p style={{ fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>
              In accordance with the Regulation, you have the right to request access to your personal data, rectify or erase your personal data, or restrict their processing. You also have the right to object to the processing of your personal data and the right to data portability.
            </p>
            <p style={{ fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>
              You can withdraw your consent for the processing of your personal data given to the Administrator at any time. However, withdrawal of consent does not affect the lawful processing of your personal data prior to the withdrawal.
            </p>
            <p style={{ fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>
              If you believe that the processing of your personal data violates the Regulation, you have the right to file a complaint with the Supervisory Authority, in addition to other rights.
            </p>
            <p style={{ fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>
              Providing your personal data is not obligatory. It is neither a legal nor a contractual requirement, nor is it required to enter into a contract.
            </p>
            <p style={{ fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
              You have the right to object to the processing of your personal data for direct marketing purposes, including profiling related to such direct marketing. If you object to the processing of your personal data for direct marketing purposes, your personal data will no longer be processed for this purpose.
            </p>
            
            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1rem' }}>7. Compliance with GDPR (General Data Protection Regulation)</h4>
            <p style={{ fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>
              This data processing policy is in full compliance with the General Data Protection Regulation (GDPR) – European Regulation (EU) 2016/679, which ensures the protection of individuals with regard to the processing of personal data and the free movement of data. The Administrator is committed to upholding the principles and requirements outlined in the GDPR to safeguard your personal data and respect your rights as a data subject. If you have any concerns or inquiries regarding the processing of your personal data, please do not hesitate to contact us.
            </p>
            
            <div style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
              <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>As a data subject under the General Data Protection Regulation (GDPR), you have the following rights concerning the processing of your personal data:</p>
              
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Right to Access:</strong> You have the right to request access to your personal data held by the Administrator. This includes information about the purposes of the processing, the categories of personal data concerned, and the recipients of the data.</p>
              
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Right to Rectification:</strong> You can request the correction or rectification of inaccurate or incomplete personal data.</p>
              
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Right to Erasure (Right to be Forgotten):</strong> You have the right to request the erasure of your personal data under specific circumstances, such as when the data is no longer necessary for the purposes it was collected or if you withdraw your consent.</p>
              
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Right to Restriction of Processing:</strong> You can request the restriction of processing your personal data in certain situations, such as when the accuracy of the data is contested, or the processing is unlawful.</p>
              
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Right to Object:</strong> You have the right to object to the processing of your personal data, including profiling, for direct marketing purposes. The Administrator will cease processing your data for this purpose upon your objection.</p>
              
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Right to Data Portability:</strong> You can request to receive your personal data, which you have provided to the Administrator, in a structured, commonly used, and machine-readable format, and have the right to transmit this data to another data controller.</p>
              
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Right to Withdraw Consent:</strong> If the processing of your personal data is based on your consent, you have the right to withdraw your consent at any time. However, this withdrawal will not affect the lawfulness of the processing carried out before the withdrawal.</p>
              
              <p style={{ margin: '0 0 0.5rem 0' }}><strong>Right to Lodge a Complaint:</strong> If you believe that the processing of your personal data violates the GDPR, you have the right to lodge a complaint with the relevant Supervisory Authority.</p>
            </div>
            
            <p style={{ fontSize: '0.8rem', margin: '1.5rem 0 0 0', fontWeight: '600', textAlign: 'center' }}>
              <strong>Recberry s.r.o.</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
